import { exportDB, importInto } from 'dexie-export-import'
import type { PosDatabase } from '@core/db/db'
import type { BackupStorage } from './BackupStorage'
import { debugLog } from '@core/utils/log'

/** Интервал авто-бэкапа: 30 минут. */
export const BACKUP_INTERVAL_MS = 30 * 60 * 1000

/**
 * Служба отказоустойчивости локальной БД.
 *
 * - backup(): полный экспорт Dexie → JSON-строка → изолированное хранилище.
 * - start(): планирует авто-бэкап каждые BACKUP_INTERVAL_MS.
 * - restoreIfNeeded(): при старте восстанавливает данные из последнего снимка,
 *   если основные stores пусты/повреждены (защита от очистки IndexedDB ОС).
 *
 * Работает только через интерфейс BackupStorage, поэтому нативное
 * Capacitor-хранилище подключится без изменений в этом классе.
 */
export class BackupService {
  private timer: ReturnType<typeof setInterval> | null = null

  constructor(
    private readonly db: PosDatabase,
    private readonly storage: BackupStorage
  ) {}

  /** Сделать снимок БД и сохранить в изолированное хранилище. */
  async backup(): Promise<void> {
    const blob = await exportDB(this.db, { prettyJson: false })
    const json = await blob.text()
    await this.storage.save(json)
    debugLog(`[backup] снимок сохранён (${json.length} байт)`)
  }

  /**
   * Восстановить данные из последнего снимка, если основные данные отсутствуют.
   * @returns true, если восстановление выполнено.
   */
  async restoreIfNeeded(): Promise<boolean> {
    const needsRestore = await this.isDataMissing()
    if (!needsRestore) {
      return false
    }
    const json = await this.storage.load()
    if (!json) {
      debugLog('[backup] восстановление не требуется: снимков нет')
      return false
    }
    const blob = new Blob([json], { type: 'application/json' })
    await importInto(this.db, blob, {
      acceptVersionDiff: true,
      clearTablesBeforeImport: true,
      // backups не восстанавливаем — это служебный store самого бэкапа.
      filter: (table) => table !== 'backups'
    })
    debugLog('[backup] данные восстановлены из последнего снимка')
    return true
  }

  /** Запустить периодический авто-бэкап. */
  start(): void {
    if (this.timer) {
      return
    }
    this.timer = setInterval(() => {
      void this.backup().catch((err) => console.error('[backup] ошибка авто-бэкапа', err))
    }, BACKUP_INTERVAL_MS)
    debugLog(`[backup] авто-бэкап запланирован каждые ${BACKUP_INTERVAL_MS / 60000} мин`)
  }

  /** Остановить авто-бэкап. */
  stop(): void {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  /** Признак того, что основные данные отсутствуют (пустая/сброшенная БД). */
  private async isDataMissing(): Promise<boolean> {
    const products = await this.db.products.count()
    const receipts = await this.db.receipts.count()
    return products === 0 && receipts === 0
  }
}
